import {Component, computed, inject, input, Signal} from '@angular/core';
import {DataBatchFacade} from '@selpia/data-batch/data-access';
import {PerspectiveFacade} from '@selpia/perspective/data-access';
import {AppConfigFacade, DimensionHelper} from '@selpia/app-config/data-access';
import {OptionsFacade} from '@selpia/options/data-access';
import {NavigationService} from '@selpia/data-batch/history-access';
import {DataChunk, DataPointParams, Measure, Sentiment} from '@selpia/shared/models';
import {DataBatchTools, SortingTools} from '@selpia/shared/utils';
import {DrilldownTarget} from '@selpia/data-batch/ui';

@Component({
  selector: 'lib-tile-preview',
  template: '',
})
export class TilePreviewComponent {
  protected readonly dataBatchFacade: DataBatchFacade = inject(DataBatchFacade);
  protected readonly perspectiveFacade: PerspectiveFacade = inject(PerspectiveFacade);
  protected readonly appConfigFacade: AppConfigFacade = inject(AppConfigFacade);
  protected readonly optionsFacade = inject(OptionsFacade);
  protected readonly navigationService = inject(NavigationService);

  readonly reportPath = input<string>();

  protected readonly isLoading: Signal<boolean> = this.dataBatchFacade.isLoading;

  protected readonly highlightedIds = this.dataBatchFacade.highlightedIds;

  protected readonly perspective = this.perspectiveFacade.perspective;
  protected readonly totalPerspective = computed(() => {
    const perspective = this.perspective();
    const dimensions = this.appConfigFacade.dimensions();
    const currentLevel = perspective?.focusDimensionLevel;

    if (!dimensions || !perspective || !currentLevel) {
      return undefined;
    }

    const dimension = DimensionHelper.getDimensionOf(currentLevel, dimensions);
    const focusDimensionLevel = DimensionHelper.getMatchingLevel(dimension, Math.max(currentLevel.levelNumber - 1, 0));
    return {...perspective, focusDimensionLevel};
  });

  protected readonly focusMeasure: Signal<Measure | undefined> = this.optionsFacade.focusMeasure;
  protected readonly focusPeriod: Signal<string | undefined> = this.optionsFacade.focusPeriod;
  protected readonly focusDate: Signal<Date | undefined> = this.perspectiveFacade.date;

  protected readonly focusParams: Signal<DataPointParams> = computed(() => {
    return {
      measureCode: this.focusMeasure()?.code,
      periodCode: this.focusPeriod(),
      date: this.focusDate(),
    };
  });

  protected readonly filteredDataChunks: Signal<DataChunk[] | undefined> = computed(() => {
    const dataChunks = this.dataBatchFacade.dataChunks();
    const periodCode = this.focusPeriod();
    const date = this.focusDate();

    return dataChunks?.filter(dataChunk => dataChunk.dataPoints.some(dataPoint => DataBatchTools.filterDataPoint(dataPoint, {
      date,
      periodCode,
    })));
  });

  protected readonly sortedDataChunks = computed(() => {
    const sortType = this.optionsFacade.sortType();
    const dataChunks = this.filteredDataChunks();

    if (!sortType || !dataChunks || dataChunks.length < 2) {
      return dataChunks;
    }
    const perspective = this.perspectiveFacade.perspective();
    const measureCode = this.optionsFacade.focusMeasure()?.code;
    const period = this.focusPeriod();
    const date = this.focusDate();
    if (!perspective || !measureCode || !period || !date) {
      return dataChunks;
    }

    const sorting = SortingTools.createSortingStrategyFromId(sortType);
    return sorting(dataChunks, {
      perspective,
      measureCode,
      period,
      date,
    });
  });


  protected readonly dimensionLevelName: Signal<string | undefined> = computed(
    () => this.perspectiveFacade.perspective()?.focusDimensionLevel?.name,
  );

  handleDrilldown(target: DrilldownTarget) {
    this.navigationService.updatePerspective({
      focusDimensionLevel: target.targetLevel,
      filterDimensions: target.dimensionItems,
    });
  }

  protected readonly Sentiment = Sentiment;

  protected getMinMax(
    measureCode: string | undefined,
    periodCode: string | undefined,
    dataChunks: DataChunk[],
  ): { min: number, max: number } {
    const values = dataChunks
      .reduce((acc, dataChunk) => {
        dataChunk.dataPoints.forEach(dataPoint => {
          if (DataBatchTools.filterDataPoint(dataPoint, {periodCode, measureCode})) {
            acc.push(dataPoint.value);
          }
        });
        return acc;
      }, <number[]>[]);

    return {min: Math.min(...values), max: Math.max(...values)};
  }
}
